import {
  ENTER_KEY,
  KEY_LABEL,
  QUICK_NAV_OPEN_SOURCE,
} from "components/CommandPalette/constants";
import { useCommandPalette } from "components/CommandPalette/CommandPaletteContext";
import { useCommandPaletteShortcut } from "components/CommandPalette/useCommandPaletteShortcut";
import Command from "components/DesignSystem/Command/Command";
import Modal from "components/DesignSystem/Modal/Modal";
import {
  QUICK_NAV_OPENED,
  QUICK_NAV_PAGE_OPENED,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useDeviceInfo } from "hooks/useDeviceInfo";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { QuickNavPage } from "constants/quickNav/types";
import { useHasCommandPalette } from "hooks/useHasCommandPalette";
import { useQuickNavLinks } from "./useQuickNavLinks";

type QuickNavModalProps = {
  pages: QuickNavPage[];
};

type QuickNavContentProps = QuickNavModalProps & {
  onClose: () => void;
};

const QuickNavContent = ({ pages, onClose }: QuickNavContentProps) => {
  const history = useHistory();
  const { isMac } = useDeviceInfo();
  const links = useQuickNavLinks(pages);
  const { trackEvent } = useAnalytics();

  const [search, setSearch] = useState("");
  const [activeItem, setActiveItem] = useState("");

  const modifierKey = isMac ? KEY_LABEL.MAC_MODIFIER : KEY_LABEL.MODIFIER;
  const activeLink = links.find(({ value }) => value === activeItem);

  const trackPageOpened = (isNewTab: boolean) => {
    if (!activeLink) return;

    trackEvent(QUICK_NAV_PAGE_OPENED, {
      page_name: activeLink.name,
      link: activeLink.to,
      search_query: search,
      is_new_tab: isNewTab,
    });
  };

  const handleNavigate = (to: string) => {
    trackPageOpened(false);
    history.push(to);
    onClose();
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey;
    trackPageOpened(isModifiedClick);

    if (!isModifiedClick) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const isNewTabEnter = e.key === ENTER_KEY && (e.metaKey || e.ctrlKey);
    if (!isNewTabEnter) return;

    e.preventDefault();

    if (activeLink) {
      trackPageOpened(true);
      window.open(activeLink.to, "_blank");
    }
  };

  return (
    <Command.Root
      value={activeItem}
      onValueChange={setActiveItem}
      onKeyDown={handleKeyDown}
      loop
    >
      <Command.SearchInput
        autoFocus
        value={search}
        onValueChange={setSearch}
        placeholder="Go to page"
      />

      <Command.List className="t-h-[30rem]">
        <Command.Empty>No results found</Command.Empty>
        {links.map(({ value, name, to, icon: Icon }) => (
          <Command.LinkItem
            key={value}
            value={value}
            to={to}
            onNavigate={handleNavigate}
            onLinkClick={handleLinkClick}
            icon={<Icon />}
          >
            {name}
          </Command.LinkItem>
        ))}
      </Command.List>

      <Command.Footer>
        <Command.Shortcut keys={[KEY_LABEL.UP, KEY_LABEL.DOWN]}>
          Navigate
        </Command.Shortcut>
        <Command.Shortcut keys={[KEY_LABEL.ENTER]}>Open page</Command.Shortcut>
        <Command.Shortcut keys={[modifierKey, KEY_LABEL.ENTER]}>
          Open in new tab
        </Command.Shortcut>
      </Command.Footer>
    </Command.Root>
  );
};

const QuickNavDialog = ({ pages }: QuickNavModalProps) => {
  const { isOpen, setIsOpen } = useCommandPalette();
  const { trackEvent } = useAnalytics();

  const handleOpen = () => {
    if (isOpen) return;

    trackEvent(QUICK_NAV_OPENED, { source: QUICK_NAV_OPEN_SOURCE.SHORTCUT });
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  useCommandPaletteShortcut(handleOpen);

  return (
    <Modal.Root open={isOpen} onOpenChange={setIsOpen}>
      <Modal.Content size="large" useCustomOverlay aria-describedby={undefined}>
        <Modal.Title className="t-sr-only">Go to page</Modal.Title>
        <QuickNavContent pages={pages} onClose={handleClose} />
      </Modal.Content>
    </Modal.Root>
  );
};

export const QuickNavModal = ({ pages }: QuickNavModalProps) => {
  const { hasCustomerPalette } = useHasCommandPalette();

  if (!hasCustomerPalette) return null;

  return <QuickNavDialog pages={pages} />;
};
